import { Suspense } from 'react'
import { MapProvider } from 'react-map-gl/mapbox'
import { IntercomProvider } from 'react-use-intercom'
import { ThemeProvider } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterLuxon } from '@mui/x-date-pickers-pro/AdapterLuxon'
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AnalyticsProvider } from 'use-analytics'

import { getUserLocale } from '@/common/utils/locale'
import { Banner } from '@/components/componentLibrary'
import LoadAnimation from '@/components/loadAnimation'
import analytics from '@/modules/analytics'
import { queryClientInstance } from '@/modules/api/queryClient'
import GlobalModalProvider from '@/modules/globalModal/GlobalModalProvider'
import { Configuration } from '@/modules/initialization/config'
import Online from '@/modules/networkConnection/Online'
import theme from '@/modules/theme'
import { RootRouter } from '@/modules/urlRouting/routes'

const App = () => {
  return (
    <QueryClientProvider client={queryClientInstance}>
      <ThemeProvider theme={theme}>
        <Suspense fallback={<LoadAnimation />}>
          <Configuration>
            <SentryErrorBoundary
              fallback={
                <Banner
                  styleType="high-urgency"
                  text="An error has occurred and has been logged. Please reload the page."
                  className="empty-state-banner"
                  buttonText="Reload"
                  buttonOnClick={() => window.location.reload()}
                />
              }
            >
              <Online>
                <AnalyticsProvider instance={analytics}>
                  <IntercomProvider appId={import.meta.env.VITE_INTERCOM_APP_ID}>
                    <MapProvider>
                      <LocalizationProvider
                        dateAdapter={AdapterLuxon}
                        adapterLocale={getUserLocale()}
                        localeText={{
                          fieldHoursPlaceholder: () => '--',
                          fieldMinutesPlaceholder: () => '--',
                          fieldMeridiemPlaceholder: () => '--',
                        }}
                      >
                        <GlobalModalProvider>
                          <RootRouter />
                        </GlobalModalProvider>
                      </LocalizationProvider>
                    </MapProvider>
                  </IntercomProvider>
                </AnalyticsProvider>
              </Online>
            </SentryErrorBoundary>
          </Configuration>
        </Suspense>
        <ReactQueryDevtools buttonPosition="bottom-right" />
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export default App
